/**
 * exportUtils.js
 * Shared CSV export and print helpers for all report pages.
 * No external libraries required — pure browser APIs only.
 */

/**
 * Export data as a CSV file download.
 * @param {string} filename  - Downloaded file name (without .csv)
 * @param {Array<{key: string, label: string}>} columns - Column definitions
 * @param {Array<Object>} rows - Data rows
 */
export function exportToCSV(filename, columns, rows) {
    const escape = (val) => {
        const str = val === null || val === undefined ? '' : String(val);
        return `"${str.replace(/"/g, '""')}"`;
    };
    const header = columns.map(c => escape(c.label)).join(',');
    const lines = rows.map(row =>
        columns.map(c => escape(row[c.key])).join(',')
    );
    const csv = '\uFEFF' + [header, ...lines].join('\r\n'); // BOM for Excel UTF-8
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/**
 * Build a printable HTML string from data for use with react-to-print.
 * Used to inject a styled table into a hidden print ref.
 *
 * @param {string} title
 * @param {string} subtitle  - e.g. date range
 * @param {Array<{key: string, label: string}>} columns
 * @param {Array<Object>} rows
 * @param {Array<{label: string, value: string}>} summary - optional summary cards
 * @returns {string} HTML string
 */
export function buildPrintHTML(title, subtitle, columns, rows, summary = []) {
    const summaryHtml = summary.length
        ? `<div class="summary">
            ${summary.map(s => `
              <div class="summary-card">
                <div class="summary-label">${s.label}</div>
                <div class="summary-value">${s.value}</div>
              </div>`).join('')}
           </div>`
        : '';

    const thead = columns.map(c => `<th>${c.label}</th>`).join('');
    const tbody = rows.map(row =>
        `<tr>${columns.map(c => `<td>${row[c.key] ?? ''}</td>`).join('')}</tr>`
    ).join('');

    return `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8"/>
          <title>${title}</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: Arial, sans-serif; font-size: 11px; color: #222; padding: 16px; }
            h1 { font-size: 16px; font-weight: 700; margin-bottom: 2px; }
            .subtitle { color: #666; font-size: 11px; margin-bottom: 12px; }
            .summary { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 14px; }
            .summary-card { background: #f5f5f5; border-radius: 6px; padding: 6px 12px; min-width: 100px; }
            .summary-label { font-size: 9px; color: #888; text-transform: uppercase; letter-spacing: 0.5px; }
            .summary-value { font-size: 13px; font-weight: 700; color: #0008B4; }
            table { width: 100%; border-collapse: collapse; margin-top: 4px; }
            th { background: #0008B4; color: #fff; text-align: left; padding: 6px 8px; font-size: 10px; }
            td { padding: 5px 8px; border-bottom: 1px solid #eee; font-size: 10px; }
            tr:nth-child(even) td { background: #f9f9f9; }
            @media print {
              body { padding: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <h1>${title}</h1>
          <div class="subtitle">${subtitle}</div>
          ${summaryHtml}
          <table>
            <thead><tr>${thead}</tr></thead>
            <tbody>${tbody}</tbody>
          </table>
        </body>
        </html>
    `;
}
